import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";

// Register fonts
Font.register({
  family: "Helvetica",
  fonts: [
    { src: "https://fonts.gstatic.com/s/helvetica/v1/Helvetica.ttf" },
    { src: "https://fonts.gstatic.com/s/helvetica/v1/Helvetica-Bold.ttf", fontWeight: "bold" },
  ],
});

// --- STYLES (Same as before) ---
const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 10, padding: 30, backgroundColor: "#ffffff", color: "#2c2c2c", paddingBottom: 75 },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, paddingBottom: 15, borderBottomWidth: 3, borderBottomColor: "#2c3e50", borderBottomStyle: "solid" },
  headerLeft: { flex: 1, flexDirection: "row", alignItems: "flex-start", gap: 12 },
  logoContainer: { width: 60, height: 60, borderRadius: 4, overflow: "hidden", borderWidth: 1, borderColor: "#e0e0e0", borderStyle: "solid" },
  logo: { width: "100%", height: "100%", objectFit: "cover" },
  hotelInfo: { flex: 1, marginLeft: 10 },
  hotelName: { fontSize: 18, fontWeight: "bold", color: "#2c3e50", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  hotelContact: { fontSize: 8, color: "#555", lineHeight: 1.5, marginBottom: 1 },
  headerRight: { width: 180, alignItems: "flex-end" },
  invoiceTitle: { fontSize: 16, fontWeight: "bold", color: "#2c3e50", marginBottom: 8, letterSpacing: 1 },
  invoiceBox: { backgroundColor: "#2c3e50", padding: 8, borderRadius: 3, width: "100%" },
  invoiceLabel: { fontSize: 7, color: "#bdc3c7", marginBottom: 2, textTransform: "uppercase" },
  invoiceValue: { fontSize: 10, color: "#fff", fontWeight: "bold" },
  infoGrid: { marginBottom: 20 },
  infoRow: { flexDirection: "row", gap: 15 },
  infoBox: { flex: 1, backgroundColor: "#f8f9fa", padding: 12, borderLeftWidth: 3, borderLeftColor: "#3498db", borderLeftStyle: "solid", marginBottom: 12 },
  infoTitle: { fontSize: 9, fontWeight: "bold", color: "#2c3e50", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 },
  infoDetailRow: { flexDirection: "row", marginBottom: 4 },
  infoLabel: { fontSize: 8, color: "#7f8c8d", width: "35%" },
  infoValue: { fontSize: 8, color: "#2c3e50", width: "65%", fontWeight: "bold" },
  
  // Table
  table: { marginTop: 15, marginBottom: 20 },
  tableHeader: { flexDirection: "row", backgroundColor: "#34495e", paddingVertical: 8, paddingHorizontal: 10, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
  tableHeaderText: { color: "#ecf0f1", fontSize: 9, fontWeight: "bold", textTransform: "uppercase" },
  colSlNo: { width: "8%", textAlign: "center" },
  colDesc: { width: "47%" },
  colQuantity: { width: "10%", textAlign: "center" },
  colRate: { width: "15%", textAlign: "right" },
  colAmount: { width: "20%", textAlign: "right" },
  tableRow: { flexDirection: "row", paddingVertical: 8, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: "#ecf0f1", borderBottomStyle: "solid" },
  tableRowAlt: { backgroundColor: "#f8f9fa" },
  tableCell: { fontSize: 8, color: "#2c3e50" },
  tableCellBold: { fontWeight: "bold" },
  
  // Summary
  summarySection: { marginTop: 20, flexDirection: "row", justifyContent: "flex-end" },
  summaryBox: { width: "45%", borderTopWidth: 2, borderTopColor: "#34495e", borderTopStyle: "solid", paddingTop: 10 },
  summaryRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 5, paddingHorizontal: 12 },
  summaryLabel: { fontSize: 9, color: "#7f8c8d" },
  summaryValue: { fontSize: 9, color: "#2c3e50", fontWeight: "bold" },
  summaryTotal: { backgroundColor: "#2c3e50", flexDirection: "row", justifyContent: "space-between", paddingVertical: 10, paddingHorizontal: 12, marginTop: 5, borderRadius: 3 },
  summaryTotalLabel: { fontSize: 11, color: "#ecf0f1", fontWeight: "bold", textTransform: "uppercase" },
  summaryTotalValue: { fontSize: 12, color: "#3498db", fontWeight: "bold" },
  summaryDivider: { height: 1, backgroundColor: "#ccc", marginVertical: 6 },
  
  // Footer
  footer: { position: "absolute", bottom: 20, left: 30, right: 30, flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTopWidth: 2, borderTopColor: "#34495e", borderTopStyle: "solid" },
  footerLeft: { flex: 1 },
  footerText: { fontSize: 8, color: "#7f8c8d", marginBottom: 2 },
  footerBold: { fontSize: 9, color: "#2c3e50", fontWeight: "bold" },
  footerRight: { alignItems: "flex-end" },
  footerThankYou: { fontSize: 9, color: "#3498db", fontWeight: "bold", fontStyle: "italic" },
});

const safeNum = (v) => (Number(v) ? Number(v) : 0);

const InvoiceDocument = ({ invoiceData, items, bookingDetails, hotelProfile }) => {
  
  // --- USE HOTEL PROFILE FROM PROPS ---
  const hotel = {
    name: hotelProfile?.name || "Hotel Name",
    address: hotelProfile?.address || "Hotel Address",
    phone: hotelProfile?.phone || hotelProfile?.mobile || "",
    email: hotelProfile?.email || "",
    gstin: hotelProfile?.gstin || "",
    logo: hotelProfile?.logo || "" 
  };

  const totalBase = items.reduce((acc, item) => acc + (parseFloat(item.base) || 0), 0);
  const totalSGST = items.reduce((acc, item) => acc + (parseFloat(item.sgst) || 0), 0);
  const totalCGST = items.reduce((acc, item) => acc + (parseFloat(item.cgst) || 0), 0);
  const totalGrand = totalBase + totalSGST + totalCGST;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        
        {/* --- HEADER --- */}
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            <View style={styles.logoContainer}>
              {hotel.logo ? (
                <Image src={hotel.logo} style={styles.logo} />
              ) : (
                <Text style={{ fontSize: 8, padding: 6, color: "#7f8c8d" }}>LOGO</Text>
              )}
            </View>

            <View style={styles.hotelInfo}>
              <Text style={styles.hotelName}>{hotel.name}</Text>
              <Text style={styles.hotelContact}>{hotel.address}</Text>
              <Text style={styles.hotelContact}>Ph: {hotel.phone}</Text>
              <Text style={styles.hotelContact}>Email: {hotel.email}</Text>
              {hotel.gstin && <Text style={styles.hotelContact}>GSTIN: {hotel.gstin}</Text>}
            </View>
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.invoiceTitle}>PROFORMA INVOICE</Text>

            <View style={styles.invoiceBox}>
              <Text style={styles.invoiceLabel}>Invoice No.</Text>
              <Text style={styles.invoiceValue}>{invoiceData.invoiceNumber}</Text>
            </View>

            <View style={[styles.invoiceBox, { marginTop: 5, backgroundColor: "#34495e" }]}>
              <Text style={styles.invoiceLabel}>Date</Text>
              <Text style={styles.invoiceValue}>{moment(invoiceData.invoiceDate).format("DD-MMM-YYYY")}</Text>
            </View>
          </View>
        </View>

        {/* --- INFO GRID --- */}
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            
            {/* Left: Bill To */}
            <View style={styles.infoBox}>
              <Text style={styles.infoTitle}>Bill To</Text>
              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Name:</Text>
                <Text style={styles.infoValue}>{invoiceData.guestName}</Text>
              </View>
              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Address:</Text>
                <Text style={styles.infoValue}>{invoiceData.guestAddress || "-"}</Text>
              </View>
              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Mobile:</Text>
                <Text style={styles.infoValue}>{invoiceData.guestMobile || "-"}</Text>
              </View>
              {invoiceData.guestGstin && (
                <View style={styles.infoDetailRow}>
                  <Text style={styles.infoLabel}>GSTIN:</Text>
                  <Text style={styles.infoValue}>{invoiceData.guestGstin}</Text>
                </View>
              )}
            </View>

            {/* Right: Stay Details */}
            <View style={[styles.infoBox, { borderLeftColor: "#e74c3c" }]}>
              <Text style={styles.infoTitle}>Stay Details</Text>
              
              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Booking Ref:</Text>
                <Text style={styles.infoValue}>{bookingDetails.booking_id}</Text>
              </View>

              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Check-in:</Text>
                <Text style={styles.infoValue}>
                   {moment.unix(bookingDetails.checkin_date).format("DD-MMM-YYYY")}
                </Text>
              </View>

              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Check-out:</Text>
                <Text style={styles.infoValue}>
                   {moment.unix(bookingDetails.checkout_date).format("DD-MMM-YYYY")}
                </Text>
              </View>

              <View style={styles.infoDetailRow}>
                <Text style={styles.infoLabel}>Total Guests:</Text>
                <Text style={styles.infoValue}>
                   {bookingDetails.total_adults} Adults, {bookingDetails.total_childs} Kids
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* --- ITEMS TABLE --- */}
        <View style={styles.table}>
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.tableHeaderText, styles.colSlNo]}>#</Text>
            <Text style={[styles.tableHeaderText, styles.colDesc]}>Description</Text>
            <Text style={[styles.tableHeaderText, styles.colRate]}>Taxable Rate</Text>
            <Text style={[styles.tableHeaderText, styles.colQuantity]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.colAmount]}>Taxable Amt</Text>
          </View>

          {items.map((it, idx) => {
            const baseRate = safeNum(it.base) / (safeNum(it.qty) || 1);

            return (
              <View key={idx} style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : null]}>
                <Text style={[styles.tableCell, styles.colSlNo]}>{idx + 1}</Text>
                <Text style={[styles.tableCell, styles.colDesc]}>
                   {it.description}
                   <Text style={{color: '#7f8c8d', fontSize: 7}}> (GST {it.taxRate}%)</Text>
                </Text>
                <Text style={[styles.tableCell, styles.colRate]}>
                   {baseRate.toFixed(2)}
                </Text>
                <Text style={[styles.tableCell, styles.colQuantity]}>
                   {safeNum(it.qty)}
                </Text>
                <Text style={[styles.tableCell, styles.colAmount, styles.tableCellBold]}>
                   {safeNum(it.base).toFixed(2)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* --- SUMMARY SECTION --- */}
        <View style={styles.summarySection} wrap={false}>
          <View style={styles.summaryBox}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Taxable Value</Text>
              <Text style={styles.summaryValue}>{totalBase.toFixed(2)}</Text>
            </View>

            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>SGST</Text>
              <Text style={styles.summaryValue}>{totalSGST.toFixed(2)}</Text>
            </View>

            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>CGST</Text>
              <Text style={styles.summaryValue}>{totalCGST.toFixed(2)}</Text>
            </View>

            <View style={styles.summaryDivider} />

            <View style={styles.summaryTotal}>
              <Text style={styles.summaryTotalLabel}>Grand Total</Text>
              <Text style={styles.summaryTotalValue}>₹ {totalGrand.toFixed(2)}</Text>
            </View>
          </View>
        </View>

        {/* --- FOOTER --- */}
        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <Text style={styles.footerText}>
              Generated on: {moment().format("DD-MMM-YYYY HH:mm")}
            </Text>
            <Text style={styles.footerBold}>
              Queries: {hotel.email || hotel.phone}
            </Text>
          </View>

          <View style={styles.footerRight}>
            <Text style={styles.footerThankYou}>Thank you for choosing us!</Text>
            <Text style={[styles.footerText, { marginTop: 3 }]}>{hotel.name}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoiceDocument;